import type { DesignSystem, SceneHandoff, ScenePlan } from './htmlDesignSlice';

import type { RootState } from 'js/src/libs/RootState';

export const getCodeoEditModeSceneId = (state: RootState): string | null =>
    state.htmlDesign?.activeConversationTarget?.sceneId ?? null;

export const getHtmlDesignPlan = (state: RootState, sceneId: string): ScenePlan | null =>
    state.htmlDesign?.planBySceneId?.[sceneId] ?? null;

export const getHtmlDesignSystem = (state: RootState): DesignSystem | null =>
    state.htmlDesign?.designSystem ?? null;

const NO_TECHNIQUES: string[] = [];

export const getSceneTechniques = (state: RootState, sceneId: string): string[] =>
    state.htmlDesign?.techniquesBySceneId?.[sceneId] ?? NO_TECHNIQUES;

/** Empty after a reload, which costs variety on the remaining scenes and nothing else. */
export const getEarlierScenePlans = (
    state: RootState,
    exceptSceneId: string,
): { text: string[]; skills: string[] }[] =>
    Object.entries(state.htmlDesign?.planBySceneId ?? {})
        .filter(([sceneId]) => sceneId !== exceptSceneId)
        .map(([sceneId, plan]) => ({
            text: plan.text,
            skills: state.htmlDesign?.techniquesBySceneId?.[sceneId] ?? [],
        }));

/** Scoped the same way as `getEarlierScenePlans`: on a re-plan, what this scene is already built
 *  with is not something an earlier scene used. */
export const getAssignedTechniques = (
    state: RootState,
    exceptSceneId: string,
): ReadonlySet<string> =>
    new Set(
        Object.entries(state.htmlDesign?.techniquesBySceneId ?? {})
            .filter(([sceneId]) => sceneId !== exceptSceneId)
            .flatMap(([, skills]) => skills),
    );

const NO_HANDOFFS: SceneHandoff[] = [];

/** The planned seams on either side of a scene: what it opens on, and what it leaves for the next. */
export const getSceneHandoffs = (
    state: RootState,
    sceneId: string,
): { incoming: SceneHandoff[]; outgoing: SceneHandoff[] } => {
    const handoffs = state.htmlDesign?.designSystem?.handoffs ?? NO_HANDOFFS;
    return {
        incoming: handoffs.filter((handoff) => handoff.toSceneId === sceneId),
        outgoing: handoffs.filter((handoff) => handoff.fromSceneId === sceneId),
    };
};
